import { AnnaApp, Loader, WindowManager } from "@anna-money/anna-web-ui";
import classNames from "classnames";
import { observer } from "mobx-react-lite";
import type { FC } from "react";
import { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useServiceFactory } from "../../app/appServices";
import { Status } from "../../components/status/status";
import type {
  ObPaymentPageFormType,
  ObPaymentPageViewStore,
} from "../obPaymentPage/obPaymentPageView/obPaymentPageViewStore";
import { formatValue, parseValue } from "../paymentLink/components/form/formStore";
import { Pp2Banner } from "./banner/pp2Banner";
import { Pp2Header } from "./header/pp2Header";
import { useFailedImages } from "./header/useFailedImages";
import { Pp2PayForm } from "./payForm/pp2PayForm";
import { Pp2SideArea } from "./pp2SideArea";
import type { Pp2ViewModel } from "./pp2ViewModel";
import { buildPp2ViewModel } from "./pp2ViewModel";
import type { Pp2CopyFeedback } from "./shared/useCopyFeedback";
import { useCopyFeedback } from "./shared/useCopyFeedback";
import { useLondonNow } from "./shared/useLondonNow";
import styles from "./pp2View.module.scss";

type Pp2LayoutProps = {
  vm: Pp2ViewModel;
  loaded: ObPaymentPageFormType;
  store: ObPaymentPageViewStore;
  copy: Pp2CopyFeedback;
};

const Pp2Layout: FC<Pp2LayoutProps> = ({ vm, loaded, store, copy }) => {
  // A hero or logo that fails to load counts as unset, for the container padding too (R3, R4)
  const images = useFailedImages();
  const heroUrl = images.usable(vm.heroUrl);
  return (
    <div className={styles.page}>
      <div
        className={classNames(styles.container, heroUrl && styles.hasHero, vm.twoColumnOnDesktop && styles.twoColumn)}
        data-test-id="pp2-page"
        data-layout={vm.twoColumnOnDesktop ? "two-column" : "single-column"}
      >
        <header>
          <Pp2Header vm={vm} heroUrl={heroUrl} logoUrl={images.usable(vm.logoUrl)} onImageError={images.fail} />
        </header>
        <div className={styles.divider} data-test-id="pp2-header-divider" />
        <main className={styles.grid}>
          <div className={styles.formColumn} data-test-id="pp2-form">
            <Pp2PayForm vm={vm} pageData={loaded.pageData} form={loaded.form} store={store} copy={copy} />
          </div>
          <Pp2SideArea vm={vm} copy={copy} />
        </main>
        <Pp2Banner url={vm.bannerUrl} />
      </div>
    </div>
  );
};

/** Public payment page v2, `/pp2/:id` (docs/pp2-spec.md). Reuses the /pp store unchanged. */
export const Pp2View: FC = observer(() => {
  const store = useServiceFactory((x) => x.createObPaymentPageViewStore());
  const params = useParams();
  const copy = useCopyFeedback();
  const now = useLondonNow();
  const loaded = store.state === "loading" || store.state === "error" ? null : store.state;

  useEffect(() => {
    if (store.redirectUrl) {
      window.location.replace(store.redirectUrl);
    }
  }, [store.redirectUrl]);

  useEffect(() => {
    void store.load(params.id ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally runs only on mount
  }, []);

  const vm = useMemo(() => (loaded ? buildPp2ViewModel(loaded.pageData, now) : null), [loaded, now]);

  // Spec 5.2: a fixed amount is shown and sent normalised to two decimals ("5500" -> "5500.00")
  useEffect(() => {
    if (loaded && vm?.amountFixed) {
      loaded.form.onChange("amount", formatValue(parseValue(loaded.pageData.amount)));
    }
  }, [loaded, vm?.amountFixed]);

  if (store.state === "error") {
    return <Status type="error" title="Something goes wrong..." canReload />;
  }

  if (!loaded || !vm) {
    return <Loader />;
  }

  return (
    <AnnaApp>
      <WindowManager>
        <Pp2Layout vm={vm} loaded={loaded} store={store} copy={copy} />
      </WindowManager>
    </AnnaApp>
  );
});
